/**
 * RFPA / SCR daily forecast engine.
 *
 * Ports the Daily Forecast App (calc + workbook snapshot) onto METS
 * RfpaScrDaily rows. Actual days are Actual/Reconciled; remaining fly days
 * are Projected rows. Holiday rows are ignored.
 *
 * Formulas:
 *   RFPA = RFP / TAR
 *   SCR  = Sfl / (Sked - Swx - Sxo)
 *   AVG  = (RFPA + SCR) / 2
 * Rates are capped at 100%.
 */

export const COUNT_KEYS = ['rfp', 'tar', 'sfl', 'sked', 'swx', 'sxo'];

export const STRATEGY_PRESETS = [
    { name: 'Break even', target: 0.961 },
    { name: 'Buffer', target: 0.965 },
    { name: 'Stretch', target: 0.97 },
];

export const toNumber = (value, fallback = 0) => {
    if (value === null || value === undefined || value === '') return fallback;
    const n = Number(value);
    return Number.isFinite(n) ? n : fallback;
};

export const isActualRow = (row) => {
    const status = String(row?.status || '').trim();
    return status === 'Actual' || status === 'Reconciled';
};

export const isProjectedRow = (row) => {
    const status = String(row?.status || '').trim();
    return status === 'Projected';
};

/** Whole-number daily minimum, rounded up so the plan never understates the target. */
export const requiredDaily = (value) => {
    const n = Math.ceil(Math.max(0, value) - 1e-9);
    return n > 0 ? n : 0;
};

/** Whole-number quarter-end allowance, rounded down. */
export const allowedCount = (value) => Math.floor(Math.max(0, value) + 1e-9);

export const wholeCount = (value) => Math.round(Math.max(0, value));

export const formatPct = (value, digits = 2) => {
    if (value === null || value === undefined || Number.isNaN(Number(value))) return '—';
    return `${(Number(value) * 100).toFixed(digits)}%`;
};

export const dateKey = (value) => {
    if (!value) return '';
    if (value instanceof Date && !Number.isNaN(value.getTime())) {
        const y = value.getFullYear();
        const m = String(value.getMonth() + 1).padStart(2, '0');
        const d = String(value.getDate()).padStart(2, '0');
        return `${y}-${m}-${d}`;
    }
    return String(value).slice(0, 10);
};

export const formatDisplayDate = (iso) => {
    if (!iso || iso === '—') return '—';
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
    if (!m) return iso;
    return `${parseInt(m[2], 10)}/${parseInt(m[3], 10)}/${m[1]}`;
};

export const buildSnapshot = (rows) => {
    const list = Array.isArray(rows) ? rows : [];
    const actuals = list.filter(isActualRow);
    const projected = list.filter(isProjectedRow);
    const totals = {};
    COUNT_KEYS.forEach((key) => {
        totals[key] = actuals.reduce((sum, row) => sum + toNumber(row[key]), 0);
    });
    const actualDays = actuals.length;
    const divisor = Math.max(1, actualDays);
    const averages = {};
    COUNT_KEYS.forEach((key) => {
        averages[key] = totals[key] / divisor;
    });
    const available = Math.max(0, totals.sked - totals.swx - totals.sxo);
    const rfpa = totals.tar ? Math.min(1, totals.rfp / totals.tar) : 1;
    const scr = available ? Math.min(1, totals.sfl / available) : 1;
    const lastActual = actuals.reduce((latest, row) => {
        const key = dateKey(row.date || row._date);
        if (!key) return latest;
        return !latest || key > latest ? key : latest;
    }, '');

    return {
        actual_days: actualDays,
        remaining_days: projected.length,
        last_actual: lastActual || '—',
        totals,
        averages,
        rfpa,
        scr,
        avg: (rfpa + scr) / 2,
    };
};

/**
 * Compute the remaining-quarter operating plan.
 * @param {object} unit Snapshot from buildSnapshot
 * @param {number} target Goal average as a fraction (e.g. 0.961)
 * @param {number} mix RFPA emphasis: 0 balanced, +0.01 protect RFPA, -0.01 protect SCR
 * @param {number} days Remaining fly days
 */
export const calcForecast = (unit, target, mix, days) => {
    const safeDays = Math.max(1, toNumber(days, 1));
    const t = unit.totals;
    const a = unit.averages;
    const rfpaGoal = Math.min(1, Math.max(0, target + mix));
    const scrGoal = Math.min(1, Math.max(0, 2 * target - rfpaGoal));
    const futureTar = a.tar * safeDays;
    const futureSked = a.sked * safeDays;
    const futureSwx = a.swx * safeDays;
    const futureSxo = a.sxo * safeDays;
    const futureAvail = Math.max(0, futureSked - futureSwx - futureSxo);
    const totalTar = t.tar + futureTar;
    const totalAvail = Math.max(0, t.sked - t.swx - t.sxo) + futureAvail;
    const needRfp = Math.max(0, rfpaGoal * totalTar - t.rfp);
    const needSfl = Math.max(0, scrGoal * totalAvail - t.sfl);
    const futureRfpa = futureTar ? needRfp / futureTar : 1;
    const futureScr = futureAvail ? needSfl / futureAvail : 1;
    const feasible = futureRfpa <= 1.00001 && futureScr <= 1.00001;

    return {
        target,
        rfpaGoal,
        scrGoal,
        futureTar,
        futureSked,
        futureSwx,
        futureSxo,
        futureAvail,
        needRfp,
        needSfl,
        rfpDay: needRfp / safeDays,
        sflDay: needSfl / safeDays,
        futureRfpa,
        futureScr,
        rfpMiss: futureTar - needRfp,
        scrGap: futureAvail - needSfl,
        feasible,
    };
};

export const outlookFor = (result) => {
    const requiredFuture = (result.futureRfpa + result.futureScr) / 2;
    const tight = result.feasible && (
        requiredFuture >= 0.98 || result.futureRfpa >= 0.99 || result.futureScr >= 0.99
    );
    if (!result.feasible) {
        return { key: 'infeasible', label: 'Not mathematically achievable', tight: false, requiredFuture };
    }
    if (tight) {
        return { key: 'tight', label: 'Tight — achievable', tight: true, requiredFuture };
    }
    return { key: 'achievable', label: 'Achievable', tight: false, requiredFuture };
};

export const rateTone = (value, target) => {
    if (value >= target) return 'good';
    if (value >= target - 0.015) return 'warn';
    return 'bad';
};
